import React, { useState, useMemo } from "react";
import {
    Box,
    Text,
    Button,
    Flex,
    Icon,
    Badge,
    Container,
    Input,
    InputGroup,
    InputLeftElement,
    SimpleGrid,
    HStack,
    Heading
} from "@chakra-ui/react";
import { FaFilePdf, FaDownload, FaEye, FaSearch, FaBookOpen } from "react-icons/fa";
import SEO from '../../components/SEO';

const DOCUMENTS = [
    { title: "ՀՀ Սահմանադրություն", file: "hhsahmanadrutyun.pdf", category: "Հիմնարար Օրենք" },
    { title: "ՀՀ քաղաքացիական օրենսգիրք", file: "hhqaxaqaciakanorensgirq.pdf", category: "Քաղաքացիական" },
    { title: "ՀՀ Քաղաքացիական դատավարության օրենսգիրք", file: "hhqaxdatorenq.pdf", category: "Դատավարություն" },
    { title: "ՀՀ Քրեական օրենսգիրք", file: "hhqreakanorensgirq.pdf", category: "Քրեական" },
    { title: "ՀՀ Քրեական դատավարության օրենսգիրք", file: "hhqreakandatavarucyanorensgirq.pdf", category: "Դատավարություն" },
    { title: "Վարչական իրավախախումների վերաբերյալ ՀՀ օրենսգիրք", file: "varchakaniravaxaxtum.pdf", category: "Վարչական" },
    { title: "Վարչարարության հիմունքների և վարչական վարույթի մասին ՀՀ օրենք", file: "varcharhimnuqner.pdf", category: "Վարչական" },
    { title: "ՀՀ Վարչական դատավարության օրենսգիրք", file: "hhvarchdatorenq.pdf", category: "Դատավարություն" },
    { title: "ՀՀ աշխատանքային օրենսգիրք", file: "hhashxatanqayinorengirq.pdf", category: "Աշխատանքային" },
    { title: "ՀՀ Անտառային օրենսգիրք", file: "hhantarayinorensgirq.pdf", category: "Բնապահպանություն" },
    { title: "ՀՀ Հողային օրենսգիրք", file: "hhhoxayinorengirq.pdf", category: "Անշարժ Գույք" },
    { title: "ՀՀ Գնումների մասին օրենք", file: "hhgnumnerimasinorenq.pdf", category: "Գնումներ" },
    { title: "ՀՀ Ընդերքի մասին օրենսգիրք", file: "hhynderqimasinorensgirq.pdf", category: "Բնապահպանություն" },
    { title: "ՀՀ Ընտանեկան օրենսգիրք", file: "hhyntanekanorengirq.pdf", category: "Ընտանեկան" },
    { title: "ՀՀ Սնանկության մասին օրենք", file: "hhsnankucyanmasinorenq.pdf", category: "Սնանկություն" },
    { title: "ՀՀ Ջրային օրենսգիրք", file: "jrayinorensgirq.pdf", category: "Բնապահպանություն" },
    { title: "ՀՀ օրենքը բաժնետիրական ընկերությունների մասին", file: "hhbajhnertirakanynkerucyanorenq.pdf", category: "Կորպորատիվ" },
    { title: "ՀՀ օրենքը սահմանափակ պատասխանատվությամբ ընկերություների մասին", file: "hhorenqusahmanapakpatasxamatucyan.pdf", category: "Կորպորատիվ" },
];

const DocumentsPage = () => {
    const [searchTerm, setSearchTerm] = useState("");

    const filteredDocuments = useMemo(() => {
        if (!searchTerm.trim()) return DOCUMENTS;
        const q = searchTerm.toLowerCase();
        return DOCUMENTS.filter(d => d.title.toLowerCase().includes(q) || d.category.toLowerCase().includes(q));
    }, [searchTerm]);

    const openPdf = (filename) => {
        const pdfUrl = `/assets/documents/${filename}`;
        window.open(pdfUrl, "_blank");
    };

    return (
        <Box bg="#f8fafc" minH="100vh">
            <SEO
                title="Հայաստանի Հանրապետության Օրենսդրություն | LegalTitan"
                description="Ներբեռնեք և կարդացեք Հայաստանի Հանրապետության պաշտոնական օրենսդրական փաստաթղթերը LegalTitan կայքում։"
                keywords="օրենսդրություն, ՀՀ օրենքներ, փաստաթղթեր, ներբեռնում, կարդալ"
                url="https://legaltitan.am/documents"
                image="https://legaltitan.am/assets/images/legalLogo.jpg"
                fbAppId="61557661694303"
            />

            {/* Executive Hero Header */}
            <Box
                bg="#070d1e"
                color="white"
                pt={{ base: 14, md: 20 }}
                pb={{ base: 16, md: 24 }}
                position="relative"
                overflow="hidden"
            >
                <Box
                    position="absolute"
                    top="-30%"
                    left="20%"
                    w="500px"
                    h="500px"
                    bg="radial-gradient(circle, rgba(29, 78, 216, 0.2) 0%, transparent 70%)"
                    pointerEvents="none"
                />

                <Container maxW="container.xl" px={{ base: 4, md: 8 }} position="relative" zIndex={1} textAlign="center">
                    <Badge
                        bg="rgba(223, 186, 115, 0.15)"
                        color="#dfba73"
                        border="1px solid rgba(223, 186, 115, 0.3)"
                        px={4}
                        py={1.5}
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="700"
                        letterSpacing="0.08em"
                        mb={4}
                    >
                        ՕՐԵՆՍԴՐԱԿԱՆ ԳՐԱԴԱՐԱՆ
                    </Badge>

                    <Heading
                        as="h1"
                        fontSize={{ base: "30px", sm: "40px", md: "52px" }}
                        fontWeight="800"
                        letterSpacing="-0.02em"
                        mb={5}
                    >
                        Հայաստանի Հանրապետության Օրենսդրություն
                    </Heading>

                    <Text
                        fontSize={{ base: "md", md: "lg" }}
                        color="gray.300"
                        maxW="720px"
                        mx="auto"
                        lineHeight="1.8"
                    >
                        Կարդացեք կամ ներբեռնեք ՀՀ հիմնական օրենսգրքերն ու օրենքները PDF ձևաչափով՝ մշտապես հասանելի:
                    </Text>
                </Container>
            </Box>

            {/* Search Bar & Document Grid */}
            <Box py={{ base: 12, md: 20 }} mt={{ base: "-30px", md: "-40px" }} position="relative" zIndex={2}>
                <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
                    {/* Search Bar */}
                    <Box
                        bg="white"
                        p={{ base: 4, md: 6 }}
                        borderRadius="2xl"
                        border="1px solid"
                        borderColor="gray.100"
                        boxShadow="0 15px 35px -5px rgba(15, 23, 42, 0.08)"
                        mb={{ base: 8, md: 12 }}
                    >
                        <Flex
                            direction={{ base: "column", md: "row" }}
                            justify="space-between"
                            align={{ base: "stretch", md: "center" }}
                            gap={4}
                        >
                            <InputGroup size="lg" maxW={{ base: "100%", md: "550px" }}>
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={FaSearch} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Որոնել օրենք կամ օրենսգիրք..."
                                    borderRadius="xl"
                                    bg="#f8fafc"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    _focus={{ borderColor: "brand.500", bg: "white" }}
                                    fontSize="sm"
                                />
                            </InputGroup>

                            <HStack justify={{ base: "space-between", md: "flex-end" }}>
                                <Badge
                                    bg="brand.50"
                                    color="brand.600"
                                    px={3}
                                    py={1.5}
                                    borderRadius="lg"
                                    fontSize="xs"
                                    fontWeight="700"
                                >
                                    Ընդհանուր՝ {filteredDocuments.length} Փաստաթուղթ
                                </Badge>
                                {searchTerm && (
                                    <Button size="xs" variant="ghost" onClick={() => setSearchTerm("")}>
                                        Մաքրել
                                    </Button>
                                )}
                            </HStack>
                        </Flex>
                    </Box>

                    {/* Documents Grid */}
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 6, lg: 8 }}>
                        {filteredDocuments.map((doc, index) => (
                            <Box
                                key={index}
                                bg="white"
                                p={{ base: 6, md: 7 }}
                                borderRadius="2xl"
                                border="1px solid"
                                borderColor="gray.100"
                                boxShadow="0 10px 30px -5px rgba(15, 23, 42, 0.05)"
                                display="flex"
                                flexDirection="column"
                                justifyContent="space-between"
                                minH="220px"
                                transition="all 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
                                _hover={{
                                    transform: "translateY(-6px)",
                                    boxShadow: "0 20px 40px rgba(15, 23, 42, 0.1)",
                                    borderColor: "brand.300",
                                }}
                            >
                                <Box>
                                    <Flex justify="space-between" align="center" mb={4}>
                                        <Flex
                                            w={11}
                                            h={11}
                                            borderRadius="xl"
                                            bg="red.50"
                                            color="red.500"
                                            align="center"
                                            justify="center"
                                            boxShadow="0 4px 12px rgba(239, 68, 68, 0.15)"
                                        >
                                            <Icon as={FaFilePdf} fontSize="20px" />
                                        </Flex>
                                        <Badge
                                            bg="gray.100"
                                            color="gray.700"
                                            fontSize="10px"
                                            fontWeight="700"
                                            px={2.5}
                                            py={1}
                                            borderRadius="md"
                                        >
                                            {doc.category}
                                        </Badge>
                                    </Flex>

                                    <Heading
                                        as="h3"
                                        fontSize={{ base: "md", md: "lg" }}
                                        fontWeight="700"
                                        color="gray.900"
                                        lineHeight="1.4"
                                        mb={4}
                                    >
                                        {doc.title}
                                    </Heading>
                                </Box>

                                <Flex justify="space-between" align="center" pt={4} borderTop="1px solid" borderColor="gray.100" gap={3}>
                                    <Button
                                        onClick={() => openPdf(doc.file)}
                                        size="sm"
                                        variant="outline"
                                        colorScheme="blue"
                                        borderRadius="lg"
                                        flex="1"
                                        leftIcon={<FaEye />}
                                    >
                                        Կարդալ
                                    </Button>

                                    <Button
                                        as="a"
                                        href={`/assets/documents/${doc.file}`}
                                        download
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="blue"
                                        borderRadius="lg"
                                        leftIcon={<FaDownload />}
                                    >
                                        Ներբեռնել
                                    </Button>
                                </Flex>
                            </Box>
                        ))}
                    </SimpleGrid>

                    {filteredDocuments.length === 0 && (
                        <Box textAlign="center" py={16}>
                            <Icon as={FaBookOpen} fontSize="48px" color="gray.300" mb={4} />
                            <Heading as="h3" fontSize="lg" color="gray.600" mb={2}>
                                Փաստաթղթեր չեն գտնվել
                            </Heading>
                            <Text fontSize="sm" color="gray.500">
                                Փորձեք փնտրել այլ բառերով կամ մաքրել որոնման դաշտը:
                            </Text>
                        </Box>
                    )}
                </Container>
            </Box>
        </Box>
    );
};

export default DocumentsPage;
